'use client';

import type { ComponentProps } from 'react';

import { cn } from '@polyseme/utils/cn/cn-utils';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { fr } from 'react-day-picker/locale';

import { buttonVariants } from '../button';

type CalendarProps = ComponentProps<typeof DayPicker>;

const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  formatters,
  components,
  ...props
}: CalendarProps) => (
  <DayPicker
    captionLayout={captionLayout}
    className={cn('bg-background p-3', className)}
    classNames={{
      months: 'relative flex flex-col gap-4',
      month: 'flex flex-col gap-2',
      nav: 'absolute inset-x-0 top-0 z-10 flex h-9 w-full items-center justify-between',
      button_previous: cn(buttonVariants({ variant: 'outline' }), 'size-9 p-0'),
      button_next: cn(buttonVariants({ variant: 'outline' }), 'size-9 p-0'),
      month_caption: 'flex h-9 w-full items-center justify-center px-10 mb-1',
      dropdowns: 'flex items-center justify-center gap-1.5 text-sm font-medium',
      dropdown_root:
        'relative inline-flex h-9 items-center rounded-md border border-input px-2 text-sm shadow-sm focus-within:border-ring',
      dropdown:
        'absolute inset-0 z-10 h-full w-full cursor-pointer appearance-none opacity-0',
      caption_label: cn(
        'select-none text-sm font-medium',
        captionLayout !== 'label' && 'inline-flex items-center gap-1'
      ),
      table: 'w-full border-collapse',
      weekdays: 'flex',
      weekday:
        'w-8 py-0.5 text-center text-xs font-normal text-muted-foreground',
      week: 'mt-1 flex w-full',
      day: 'relative flex h-8 w-8 items-center justify-center p-0 text-center text-sm',
      day_button: cn(
        buttonVariants({ variant: 'ghost' }),
        'size-8 p-0 font-normal aria-selected:opacity-100'
      ),
      selected:
        '[&>.rdp-day_button]:bg-primary [&>.rdp-day_button]:text-primary-foreground [&>.rdp-day_button]:hover:bg-primary [&>.rdp-day_button]:hover:text-primary-foreground rounded-md',
      today:
        '[&>.rdp-day_button]:bg-accent [&>.rdp-day_button]:text-accent-foreground rounded-md',
      outside: 'text-muted-foreground opacity-50',
      disabled: 'text-muted-foreground opacity-50',
      hidden: 'invisible',
      range_start: 'rounded-l-md bg-accent',
      range_middle: 'rounded-none',
      range_end: 'rounded-r-md bg-accent',
      ...classNames,
    }}
    components={{
      Chevron: ({
        className: chevronClassName,
        orientation,
        ...chevronProps
      }) => {
        if (orientation === 'left') {
          return (
            <ChevronLeftIcon
              className={cn('size-4', chevronClassName)}
              {...chevronProps}
            />
          );
        }
        if (orientation === 'right') {
          return (
            <ChevronRightIcon
              className={cn('size-4', chevronClassName)}
              {...chevronProps}
            />
          );
        }
        return (
          <ChevronDownIcon
            className={cn('size-3', chevronClassName)}
            {...chevronProps}
          />
        );
      },
      ...components,
    }}
    formatters={{
      formatMonthDropdown: (date) =>
        date.toLocaleString('fr-FR', { month: 'short' }),
      ...formatters,
    }}
    locale={fr}
    showOutsideDays={showOutsideDays}
    {...props}
  />
);

Calendar.displayName = 'Calendar';

export { Calendar };
export type { CalendarProps };
